import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { fileUrl } from "../lib/api";
import { mergeContent, BOARD } from "../lib/content";
import ContentPanel from "./ContentPanel";
import ImageUploader from "../components/ImageUploader";
import { local } from "../lib/storage";
import {
  LogOut, Users, Crown, Film, Handshake, FileText, Images, GraduationCap, Trophy, Inbox, Settings as SetIcon,
  Plus, Edit2, Trash2, Upload, KeyRound, Home, Save, Check, X,
} from "lucide-react";

const TABS = [
  { k: "stats", label: "نظرة عامة", icon: Home, tid: "admin-nav-stats" },
  { k: "content", label: "محتوى الموقع", icon: FileText, tid: "admin-nav-content" },
  { k: "team", label: "الفريق", icon: Users, tid: "admin-nav-team" },
  { k: "mom", label: "أعضاء الشهر", icon: Crown, tid: "admin-nav-mom" },
  { k: "plays", label: "المسرحيات", icon: Film, tid: "admin-nav-plays" },
  { k: "workshops", label: "الفعاليات والدورات", icon: GraduationCap, tid: "admin-nav-workshops" },
  { k: "achievements", label: "الإنجازات", icon: Trophy, tid: "admin-nav-achievements" },
  { k: "partners", label: "شركاء النجاح", icon: Handshake, tid: "admin-nav-partners" },
  { k: "gallery", label: "شريط الصور", icon: Images, tid: "admin-nav-gallery" },
  { k: "messages", label: "الصندوق", icon: Inbox, tid: "admin-nav-inbox" },
  { k: "settings", label: "الإعدادات", icon: SetIcon, tid: "admin-nav-settings" },
];

const inputCls = "w-full input-theater rounded-lg px-3 py-2 text-sm";
const labelCls = "text-xs text-[#BFB3A0] mb-1 block";

// ---------- Generic CRUD Panel ----------
function CrudPanel({ title, endpoint, columns, defaultItem, fields, testPrefix }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null); // item or null
  const [showForm, setShowForm] = useState(false);
  const load = React.useCallback(async () => {
    const r = await api.get(endpoint);
    setItems(r.data);
  }, [endpoint]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      const payload = { ...editing };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      if (editing.id) await api.put(`${endpoint}/${editing.id}`, payload);
      else await api.post(endpoint, payload);
      toast.success("تم الحفظ");
      setShowForm(false); setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحفظ"); }
  };

  const del = async (id) => {
    if (!window.confirm("متأكد من الحذف؟")) return;
    await api.delete(`${endpoint}/${id}`);
    toast.success("تم الحذف");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-semibold text-[#F0E8DB]">{title}</h2>
        <button
          data-testid={`${testPrefix}-add-button`}
          onClick={() => { setEditing({ ...defaultItem }); setShowForm(true); }}
          className="btn-gold rounded-full px-4 py-2 text-sm inline-flex items-center gap-1"
        >
          <Plus size={16} /> إضافة
        </button>
      </div>

      {items.length === 0 ? (
        <div className="theater-card rounded-xl p-8 text-center text-[#A89A88]">لا توجد عناصر بعد.</div>
      ) : (
        <div className="theater-card rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#241615] text-[#F0E8DB]">
              <tr>
                {columns.map((c) => <th key={c.key} className="text-right px-4 py-3 font-medium">{c.label}</th>)}
                <th className="px-4 py-3 w-32">إجراءات</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`${testPrefix}-row-${it.id}`} className="border-t border-[#F0E8DB]/10 text-[#D6CBBF]">
                  {columns.map((c) => (
                    <td key={c.key} className="px-4 py-3 max-w-xs truncate">
                      {c.render ? c.render(it) : (it[c.key] || "—")}
                    </td>
                  ))}
                  <td className="px-4 py-3">
                    <div className="flex gap-1">
                      <button data-testid={`${testPrefix}-edit-${it.id}`} onClick={() => { setEditing({ ...it }); setShowForm(true); }} className="p-2 hover:bg-[#241615] rounded text-[#F0E8DB]"><Edit2 size={14} /></button>
                      <button data-testid={`${testPrefix}-delete-${it.id}`} onClick={() => del(it.id)} className="p-2 hover:bg-[#241615] rounded text-[#943833]"><Trash2 size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && editing && (
        <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="theater-card rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-semibold text-[#F0E8DB]">{editing.id ? "تعديل" : "إضافة"} — {title}</h3>
              <button onClick={() => setShowForm(false)} className="text-[#F0E8DB] p-2 hover:bg-[#241615] rounded"><X size={18} /></button>
            </div>
            <div className="space-y-4">
              {fields.map((f) => (
                <div key={f.key}>
                  <label className={labelCls}>{f.label}</label>
                  {f.type === "textarea" ? (
                    <textarea className={`${inputCls} min-h-[100px]`} value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} data-testid={`${testPrefix}-field-${f.key}`} />
                  ) : f.type === "image" ? (
                    <ImageUploader value={editing[f.key]} onChange={(v) => setEditing({ ...editing, [f.key]: v })} testid={`${testPrefix}-upload-${f.key}`} />
                  ) : f.type === "select" ? (
                    <select className={inputCls} value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} data-testid={`${testPrefix}-field-${f.key}`}>
                      {f.options.map((o) => <option key={o.value} value={o.value} className="bg-[#241615]">{o.label}</option>)}
                    </select>
                  ) : f.type === "number" ? (
                    <input type="number" className={inputCls} value={editing[f.key] || 0} onChange={(e) => setEditing({ ...editing, [f.key]: parseInt(e.target.value) || 0 })} data-testid={`${testPrefix}-field-${f.key}`} />
                  ) : (
                    <input type={f.type || "text"} className={inputCls} value={editing[f.key] || ""} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })} placeholder={f.placeholder} data-testid={`${testPrefix}-field-${f.key}`} />
                  )}
                </div>
              ))}
              <button data-testid={`${testPrefix}-save-button`} onClick={save} className="btn-gold rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-1">
                <Save size={16} /> حفظ
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------- Stats ----------
function StatsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const cards = [
    { label: "أعضاء الفريق", value: s.team, icon: Users },
    { label: "المسرحيات", value: s.plays, icon: Film },
    { label: "الفعاليات والدورات", value: s.workshops, icon: GraduationCap },
    { label: "الإنجازات", value: s.achievements, icon: Trophy },
    { label: "الرسائل الكلية", value: s.messages_total, icon: Inbox },
    { label: "غير مقروءة", value: s.messages_unread, icon: Inbox },
  ];
  return (
    <div>
      <h2 className="text-2xl font-semibold text-[#F0E8DB] mb-6">نظرة عامة</h2>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        {cards.map((c) => {
          const Icon = c.icon;
          return (
            <div key={c.label} className="theater-card rounded-xl p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-[#7A2E2A]/30 border border-[#F0E8DB]/40 flex items-center justify-center text-[#F0E8DB]"><Icon size={20} /></div>
              <div>
                <div className="text-2xl font-semibold text-[#F0E8DB]">{c.value}</div>
                <div className="text-xs text-[#A89A88]">{c.label}</div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------- Messages ----------
function MessagesPanel() {
  const [items, setItems] = useState([]);
  const load = async () => { const r = await api.get("/messages"); setItems(r.data); };
  useEffect(() => { load(); }, []);
  const markRead = async (id) => { await api.post(`/messages/${id}/read`); load(); };
  const del = async (id) => { if (!window.confirm("حذف الرسالة؟")) return; await api.delete(`/messages/${id}`); load(); };

  return (
    <div>
      <h2 className="text-2xl font-semibold text-[#F0E8DB] mb-6">صندوق الرسائل</h2>
      {items.length === 0 ? <div className="theater-card rounded-xl p-8 text-center text-[#A89A88]">لا توجد رسائل.</div> :
        <div className="space-y-3">
          {items.map((m) => (
            <div key={m.id} data-testid={`message-${m.id}`} className={`theater-card rounded-xl p-5 ${m.read ? "opacity-70" : ""}`}>
              <div className="flex justify-between items-start gap-3 mb-2">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-[#F0E8DB] font-semibold">{m.name}</span>
                    <span className="text-xs px-2 py-0.5 rounded-full bg-[#7A2E2A]/40 text-[#F0E8DB] border border-[#F0E8DB]/30">
                      {m.type === "join" ? "طلب انضمام" : "تواصل"}
                    </span>
                    {!m.read && <span className="text-xs px-2 py-0.5 rounded-full bg-[#F0E8DB] text-[#1A0F0E]">جديد</span>}
                  </div>
                  <div className="text-xs text-[#BFB3A0] mt-1">{m.email} {m.phone && `· ${m.phone}`} {m.committee && `· ${m.committee}`}</div>
                  {m.subject && <div className="text-sm text-[#F0E8DB] mt-2">{m.subject}</div>}
                </div>
                <div className="flex gap-1">
                  {!m.read && <button data-testid={`message-read-${m.id}`} onClick={() => markRead(m.id)} className="p-2 hover:bg-[#241615] rounded text-[#F0E8DB]"><Check size={14} /></button>}
                  <button data-testid={`message-delete-${m.id}`} onClick={() => del(m.id)} className="p-2 hover:bg-[#241615] rounded text-[#943833]"><Trash2 size={14} /></button>
                </div>
              </div>
              <p className="text-[#D6CBBF] text-sm whitespace-pre-line mt-2">{m.message}</p>
              <div className="text-xs text-[#5c4e3c] mt-3">{new Date(m.created_at).toLocaleString("ar")}</div>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

// ---------- Settings ----------
function SettingsPanel({ committees }) {
  const [s, setS] = useState(null);
  const [pw, setPw] = useState({ current_password: "", new_password: "" });
  useEffect(() => { api.get("/settings").then((r) => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => {
    try {
      const payload = { ...s }; delete payload.id;
      await api.put("/settings", payload);
      toast.success("تم حفظ الإعدادات");
    } catch { toast.error("فشل الحفظ"); }
  };
  const changePw = async () => {
    try {
      await api.post("/auth/change-password", pw);
      toast.success("تم تغيير كلمة المرور");
      setPw({ current_password: "", new_password: "" });
    } catch (e) { toast.error(e.response?.data?.detail || "فشل التغيير"); }
  };
  const F = ({ k, label, type = "text", options }) => (
    <div>
      <label className={labelCls}>{label}</label>
      {options
        ? <select className={inputCls} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`settings-${k}`}>
            <option value="">— بدون —</option>
            {options.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        : type === "textarea"
        ? <textarea className={`${inputCls} min-h-[100px]`} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`settings-${k}`} />
        : <input type={type} className={inputCls} value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: type === "number" ? (e.target.value === "" ? 0 : parseInt(e.target.value, 10) || 0) : e.target.value })} data-testid={`settings-${k}`} />}
    </div>
  );

  return (
    <div>
      <h2 className="text-2xl font-semibold text-[#F0E8DB] mb-6">الإعدادات العامة</h2>
      <div className="theater-card rounded-xl p-6 space-y-4 mb-6">
        <F k="hero_tagline" label="الشعار الرئيسي (Tagline)" />
        <F k="about" label="النبذة" type="textarea" />
        <F k="mission" label="الرسالة" type="textarea" />
        <div className="grid md:grid-cols-2 gap-4">
          <F k="email" label="البريد الرسمي" />
          <F k="external_join_form_url" label="رابط Google Form الخارجي (اختياري)" />
          <F k="instagram" label="Instagram URL" />
          <F k="twitter" label="Twitter / X URL" />
          <F k="tiktok" label="TikTok URL" />
          <F k="whatsapp" label="رابط مجموعة WhatsApp" />
          <F k="members_count" label="عدد أعضاء النادي (يظهر في العدادات)" type="number" />
          <F k="best_committee" label="أفضل لجنة هذا الشهر" options={committees} />
          <F k="best_committee_reason" label="سبب اختيار أفضل لجنة" />
          <F k="youtube" label="YouTube URL" />
        </div>
        <button data-testid="admin-save-settings-button" onClick={save} className="btn-gold rounded-full px-5 py-2.5 text-sm inline-flex items-center gap-1"><Save size={16} /> حفظ الإعدادات</button>
      </div>

      <div className="theater-card rounded-xl p-6">
        <h3 className="text-lg font-semibold text-[#F0E8DB] mb-4 flex items-center gap-2"><KeyRound size={16} className="text-[#F0E8DB]" /> تغيير كلمة المرور</h3>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <input type="password" className={inputCls} placeholder="كلمة المرور الحالية" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} data-testid="pw-current" />
          <input type="password" className={inputCls} placeholder="كلمة المرور الجديدة" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} data-testid="pw-new" />
        </div>
        <button data-testid="pw-save" onClick={changePw} className="btn-primary rounded-full px-5 py-2.5 text-sm">تحديث كلمة المرور</button>
      </div>
    </div>
  );
}

// ---------- Main Dashboard ----------
export default function AdminDashboard() {
  const [content, setContent] = useState(mergeContent(null));
  useEffect(() => { api.get("/content").then((r) => setContent(mergeContent(r.data))).catch(() => {}); }, []);
  const COMMITTEE_ORDER = content.committee_list.map((c) => c.name);
  const nav = useNavigate();
  const [tab, setTab] = useState("stats");

  const logout = () => {
    local.remove("theater_token");
    local.remove("theater_email");
    nav("/admin/login");
  };

  const teamFields = [
    { key: "name", label: "الاسم" },
    { key: "role", label: "الدور" },
    { key: "committee", label: "اللجنة", type: "select", options: [BOARD, ...COMMITTEE_ORDER].map((c) => ({ value: c, label: c })) },
    { key: "bio", label: "نبذة", type: "textarea" },
    { key: "image_url", label: "الصورة", type: "image" },
    { key: "order", label: "الترتيب", type: "number" },
  ];
  const momFields = [
    { key: "committee", label: "اللجنة", type: "select", options: COMMITTEE_ORDER.map((c) => ({ value: c, label: c })) },
    { key: "member_name", label: "اسم العضو" },
    { key: "month", label: "الشهر (مثال: 2026-02)" },
    { key: "rank", label: "المركز", type: "select", options: [{ value: 1, label: "الأول" }, { value: 2, label: "الثاني" }, { value: 3, label: "الثالث" }] },
    { key: "reason", label: "السبب", type: "textarea" },
    { key: "image_url", label: "الصورة", type: "image" },
  ];
  const galleryFields = [
    { key: "image_url", label: "الصورة", type: "image" },
    { key: "caption", label: "وصف قصير (اختياري)" },
    { key: "order", label: "الترتيب", type: "number" },
  ];
  const partnerFields = [
    { key: "name", label: "اسم الشريك" },
    { key: "logo_url", label: "الشعار", type: "image" },
    { key: "url", label: "رابط الموقع (اختياري)" },
    { key: "order", label: "الترتيب", type: "number" },
  ];
  const playsFields = [
    { key: "title", label: "عنوان المسرحية" },
    { key: "description", label: "الوصف", type: "textarea" },
    { key: "date", label: "التاريخ" },
    { key: "director", label: "الإخراج" },
    { key: "cast", label: "الطاقم" },
    { key: "youtube_url", label: "رابط يوتيوب" },
    { key: "image_url", label: "صورة الغلاف", type: "image" },
  ];
  const workshopFields = [
    { key: "title", label: "العنوان" },
    { key: "description", label: "الوصف", type: "textarea" },
    { key: "date", label: "التاريخ" },
    { key: "instructor", label: "المُدرِّب" },
    { key: "status", label: "الحالة", type: "select", options: [{ value: "upcoming", label: "قادمة" }, { value: "past", label: "سابقة" }] },
    { key: "register_url", label: "رابط التسجيل" },
    { key: "image_url", label: "الصورة", type: "image" },
  ];
  const achFields = [
    { key: "title", label: "العنوان" },
    { key: "description", label: "الوصف", type: "textarea" },
    { key: "date", label: "التاريخ" },
    { key: "icon", label: "الأيقونة", type: "select", options: [
      { value: "trophy", label: "كأس" }, { value: "star", label: "نجمة" },
      { value: "sparkles", label: "بريق" }, { value: "flag", label: "علم" },
    ]},
  ];

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#120908] text-[#D6CBBF] flex">
      {/* Sidebar */}
      <aside className="w-64 border-l border-[#F0E8DB]/15 bg-[#0d0605] min-h-screen p-4 sticky top-0 h-screen overflow-y-auto">
        <div className="flex items-center gap-2 px-2 py-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#7A2E2A] to-[#3d1614] border border-[#F0E8DB]/50 flex items-center justify-center text-[#F0E8DB] font-bold">T</div>
          <div>
            <div className="text-[#F0E8DB] font-semibold text-sm">نادي المسرح</div>
            <div className="text-[#F0E8DB] text-xs font-serif-en">ADMIN</div>
          </div>
        </div>
        <nav className="space-y-1">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.k}
                data-testid={t.tid}
                onClick={() => setTab(t.k)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition ${
                  tab === t.k ? "bg-[#7A2E2A] text-[#F0E8DB]" : "text-[#C5B8A8] hover:bg-[#241615]"
                }`}
              >
                <Icon size={16} />
                {t.label}
              </button>
            );
          })}
        </nav>
        <div className="mt-6 pt-6 border-t border-[#F0E8DB]/15 space-y-1">
          <a href="/" data-testid="admin-goto-home" className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#C5B8A8] hover:bg-[#241615]">
            <Home size={16} /> الموقع
          </a>
          <button data-testid="admin-logout" onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-[#943833] hover:bg-[#241615]">
            <LogOut size={16} /> تسجيل خروج
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 p-6 sm:p-10">
        {tab === "stats" && <StatsPanel />}
        {tab === "content" && <ContentPanel onSaved={(c) => setContent(mergeContent(c))} />}
        {tab === "team" && <CrudPanel
          title="الفريق الإداري" endpoint="/team" testPrefix="team"
          defaultItem={{ name: "", role: "", committee: BOARD, bio: "", image_url: "", order: 0 }}
          columns={[
            { key: "image_url", label: "صورة", render: (r) => r.image_url ? <img src={fileUrl(r.image_url)} className="w-10 h-10 rounded-full object-cover" alt="" /> : "—" },
            { key: "name", label: "الاسم" }, { key: "role", label: "الدور" }, { key: "committee", label: "اللجنة" },
          ]}
          fields={teamFields}
        />}
        {tab === "mom" && <CrudPanel
          title="أعضاء الشهر" endpoint="/members-of-month" testPrefix="mom"
          defaultItem={{ committee: COMMITTEE_ORDER[0], member_name: "", month: "", rank: 1, reason: "", image_url: "" }}
          columns={[
            { key: "member_name", label: "الاسم" }, { key: "committee", label: "اللجنة" }, { key: "month", label: "الشهر" },
            { key: "rank", label: "المركز", render: (r) => ["الأول", "الثاني", "الثالث"][(r.rank ?? 1) - 1] },
          ]}
          fields={momFields}
        />}
        {tab === "plays" && <CrudPanel
          title="المسرحيات" endpoint="/plays" testPrefix="plays"
          defaultItem={{ title: "", description: "", date: "", director: "", cast: "", youtube_url: "", image_url: "" }}
          columns={[{ key: "title", label: "العنوان" }, { key: "date", label: "التاريخ" }, { key: "director", label: "الإخراج" }]}
          fields={playsFields}
        />}
        {tab === "workshops" && <CrudPanel
          title="الفعاليات والدورات" endpoint="/workshops" testPrefix="workshops"
          defaultItem={{ title: "", description: "", date: "", instructor: "", status: "upcoming", image_url: "", register_url: "" }}
          columns={[{ key: "title", label: "العنوان" }, { key: "date", label: "التاريخ" }, { key: "status", label: "الحالة", render: (r) => r.status === "upcoming" ? "قادمة" : "سابقة" }]}
          fields={workshopFields}
        />}
        {tab === "achievements" && <CrudPanel
          title="الإنجازات" endpoint="/achievements" testPrefix="achievements"
          defaultItem={{ title: "", description: "", date: "", icon: "trophy" }}
          columns={[{ key: "title", label: "العنوان" }, { key: "date", label: "التاريخ" }]}
          fields={achFields}
        />}
        {tab === "partners" && <CrudPanel
          title="شركاء النجاح" endpoint="/partners" testPrefix="partners"
          defaultItem={{ name: "", logo_url: "", url: "", order: 0 }}
          columns={[
            { key: "logo_url", label: "الشعار", render: (r) => r.logo_url ? <img src={fileUrl(r.logo_url)} className="w-10 h-10 rounded object-contain" alt="" /> : "—" },
            { key: "name", label: "الاسم" }, { key: "url", label: "الرابط" },
          ]}
          fields={partnerFields}
        />}
        {tab === "gallery" && <CrudPanel
          title="شريط الصور" endpoint="/gallery" testPrefix="gallery"
          defaultItem={{ image_url: "", caption: "", order: 0 }}
          columns={[
            { key: "image_url", label: "الصورة", render: (r) => r.image_url ? <img src={fileUrl(r.image_url)} className="w-16 h-12 rounded object-cover" alt="" /> : "—" },
            { key: "caption", label: "الوصف" }, { key: "order", label: "الترتيب" },
          ]}
          fields={galleryFields}
        />}
        {tab === "messages" && <MessagesPanel />}
        {tab === "settings" && <SettingsPanel committees={COMMITTEE_ORDER} />}
      </main>
    </div>
  );
}
